/**
 * THE MONTH, AS A REAL TABLE.
 *
 * ★ A REAL `<table>` WITH A REAL `<caption>` AND `scope="col"` HEADERS, not a grid of divs. The
 * component inventory's own argument for owning `NavRail` applies here word for word: the
 * prototype's cabinet navigation is `<span>`s with click handlers — "not focusable, no role, no
 * keyboard activation, no focus ring" (`docs/ui/03-components.md`) — and a calendar drawn
 * the same way is a month a screen reader cannot read. `web/ui`'s `DataTable` is the shared table
 * and is the wrong shape for this one: it is a list of ROWS with columns, and a month is a
 * two-dimensional layout where the cell's position carries the meaning.
 *
 * ★ EVERY DAY CELL IS A BUTTON WITH A FULL SENTENCE FOR A NAME. "17" tells a screen-reader user
 * nothing; "Thursday, September 17, 2026 — 2 articles scheduled across MoonX, 1 sky event" tells
 * them what three glyphs tell a sighted reader. `aria-pressed` carries the selection, so the pane
 * below needs no announcing, and all inside the button is `aria-hidden`: the sentence is its name.
 *
 * ★★ WHAT IS DELIBERATELY NOT DRAWN, AND IT IS THREE THINGS THE PROTOTYPE HAS.
 * `docs/ui/04-pages.md` §3.6 measures them in python against both specs and gets
 * `saturation 0, theme 0, pitch 0, busy 0` in each: the open/getting-busy/covered THEME CHIPS,
 * the "13 pieces already in motion · 4 themes open" line, and HEADLINE STARTERS. No theme entity
 * exists anywhere on this platform and no saturation number is on any wire — `04-pages.md` §8
 * names "calendar theme saturation" as one of the three biggest platform-side gaps still needing an
 * owner's decision. A pane marked PLATFORM WORK OWED has no operation behind it, so none of the
 * three is drawn here, faked here, or given an invented endpoint. The owner's design-15 decision 1
 * (2026-10-01, "leave out what nothing backs") keeps them out, and `../components/NotDrawn.tsx` is
 * now one plain line about the counts rather than a list of what is missing.
 *
 * WHAT IS DRAWN IN THEIR PLACE IS THE THING THE OWNER ACTUALLY RULED. `dayLoad` — how many pieces
 * are scheduled per UTC day across all authors, counts only, no names and no titles
 * (`04-pages.md`, ✅ RULED 2026-08-26) — answers the question the theme chips were reaching
 * for: *is this slot already crowded?*
 *
 * ★ THE LOOK IS THE PROTOTYPE'S (design 15, the owner's live look at real cabinets, 2026-10-01):
 * each day a rounded card on the page, the moon's mark on a day that has a moon phase, the sky's
 * events in gold, the author's own pieces as small cards with their status, and the day's count
 * across MoonX as a small pill followed by one dot per category (./category-marks.tsx). The caption
 * is still the table's name and is visually hidden: the month is printed in the ‹ › row above. On a
 * phone the cards keep the number, the moon, a gold mark for a piece of yours and the count.
 *
 * ★ TODAY (the owner's list, 2026-10-05, item 7). The day the platform's clock is in - a response,
 * never the device's clock (./calendar-today.ts) - gets a gold ring round its number when it is in
 * the month on screen, and its button says so twice for assistive tech: `aria-current="date"`, and
 * "today" in its sentence, after the date. Another month, a read that failed or has not landed:
 * no day is marked.
 */
import { VisuallyHidden } from '@mantine/core';
import { FileText, Moon } from '@moonx/ui';
import { statusLabel } from '../../cabinet/components';
import { useTranslation } from '../../../i18n';
import type { TFunction } from 'i18next';
import { dayNumber, daysOfMonth, displayLocale, leadingBlanks, monthLabel } from '../domain/month';
import type { MonthKey } from '../domain/month';
import type { AuthorCalendarArticle, AuthorCalendarDayLoad, AuthorCalendarEvent } from '../api';
import { useCalendarToday } from './calendar-today';
import { CategoryDot } from './category-marks';
import type { CategoryMark } from './category-marks';
import classes from './calendar.module.css';

/** Monday first, named in the cabinet's language. A rendering choice, not a platform fact — every
 *  DAY below is still the server's UTC day, which is what `dayLoad[].day` and `daysOfMonth` speak. */
function weekdays(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  // 2024-01-01 was a Monday.
  return Array.from({ length: 7 }, (_, index) =>
    format.format(new Date(Date.UTC(2024, 0, 1 + index))),
  );
}

/** Everything that falls on one UTC day, joined on the `YYYY-MM-DD` string and never on a
 *  re-derived Date — two Dates built from the same day can differ by a zone. */
export interface DayBucket {
  readonly day: string;
  readonly events: readonly AuthorCalendarEvent[];
  readonly articles: readonly AuthorCalendarArticle[];
  readonly load: AuthorCalendarDayLoad | undefined;
}

/** The UTC day an instant falls on. A PARSE of a value the server gave us, which C11 exempts by
 *  name (`eslint.config.mjs`). */
function utcDayOf(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
}

/**
 * Bucket one month's response into day cells.
 *
 * ★ AN ALL-DAY INTERVAL IS PLACED ON EVERY DAY IT COVERS, and a instant on one. `endsAt` is
 * EXCLUSIVE and "**Null means an instant** rather than a zero-length interval — there is no
 * separate flag for it" (`authors-api.yaml`), so the loop below stops BEFORE `endsAt`.
 * Drawing "Equinox week" on its first day only would hide six of the seven days it is about.
 */
export function bucketByDay(
  month: MonthKey,
  events: readonly AuthorCalendarEvent[],
  articles: readonly AuthorCalendarArticle[],
  dayLoad: readonly AuthorCalendarDayLoad[],
): readonly DayBucket[] {
  const loadByDay = new Map(dayLoad.map((row) => [row.day, row]));
  const eventsByDay = new Map<string, AuthorCalendarEvent[]>();
  for (const event of events) {
    const start = utcDayOf(event.startsAt);
    if (start === undefined) {
      continue;
    }
    const endExclusive = utcDayOf(event.endsAt ?? undefined);
    let day = start;
    // A guard, not a belief: a malformed pair from a future deployment must not spin here.
    for (let step = 0; step < 366; step += 1) {
      const bucket = eventsByDay.get(day);
      if (bucket === undefined) {
        eventsByDay.set(day, [event]);
      } else {
        bucket.push(event);
      }
      if (endExclusive === undefined) {
        break;
      }
      const next = new Date(`${day}T00:00:00.000Z`).getTime() + 86_400_000;
      day = new Date(next).toISOString().slice(0, 10);
      if (day >= endExclusive) {
        break;
      }
    }
  }

  const articlesByDay = new Map<string, AuthorCalendarArticle[]>();
  for (const article of articles) {
    // A published piece is placed on the day it went out; a scheduled one on the day it is due.
    // `publishedAt` wins where both are set, because that is the day that happened.
    const day = utcDayOf(article.publishedAt ?? article.goLiveAt);
    if (day === undefined) {
      continue;
    }
    const bucket = articlesByDay.get(day);
    if (bucket === undefined) {
      articlesByDay.set(day, [article]);
    } else {
      bucket.push(article);
    }
  }

  return daysOfMonth(month).map((day) => ({
    day,
    events: eventsByDay.get(day) ?? [],
    articles: articlesByDay.get(day) ?? [],
    load: loadByDay.get(day),
  }));
}

/** The sentence a screen reader hears. Built from the same three numbers the cell draws, so the two
 *  cannot drift. Plain words (design 15): "across MoonX", never "across the platform". Today's
 *  sentence says "today" straight after the date, so a day is still found by its date first. */
function cellLabel(
  bucket: DayBucket,
  locale: string,
  today: boolean,
  t: TFunction<'calendar'>,
): string {
  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' }).format(
    new Date(`${bucket.day}T00:00:00.000Z`),
  );
  const parts: string[] = today ? [t('grid.today')] : [];
  parts.push(t('grid.scheduled', { count: bucket.load?.scheduled ?? 0 }));
  if (bucket.events.length > 0) {
    parts.push(t('grid.skyEvents', { count: bucket.events.length }));
  }
  if (bucket.articles.length > 0) {
    parts.push(t('grid.ownArticles', { count: bucket.articles.length }));
  }
  return t('grid.cell', { date, parts: parts.join(t('grid.partsSeparator')) });
}

/**
 * How crowded a day looks, in four bands.
 *
 * ★ THE BANDS ARE A RENDERING OF A COUNT AND NOT A PLATFORM VALUE. There is no saturation number on
 * any wire — that is exactly the gap `04-pages.md` §8 names — so these thresholds are this
 * screen's own reading of `dayLoad[].scheduled`, and the number itself is on the cell beside them.
 * A reader is never shown a band without the count that produced it. (Design 15: the band is how
 * full the count's pill is - ./calendar.module.css `.loadSteady` / `.loadBusy` / `.loadCrowded`.)
 */
function loadBand(scheduled: number): 'quiet' | 'steady' | 'busy' | 'crowded' {
  if (scheduled === 0) {
    return 'quiet';
  }
  if (scheduled <= 2) {
    return 'steady';
  }
  return scheduled <= 5 ? 'busy' : 'crowded';
}

const BAND_CLASS = {
  quiet: undefined,
  steady: classes.loadSteady,
  busy: classes.loadBusy,
  crowded: classes.loadCrowded,
} as const;

/** The sky event kind the moon's mark stands for. A kind code, which the contract fixes by
 *  compatibility (`authors-api.yaml`, `events[].kind`); the event's own title says which phase. */
const LUNAR_PHASE_KIND = 'lunar_phase';

/** How many events and pieces a day card shows before "+N more"; the pane always shows them all. */
const SHOWN_PER_DAY = 2;

export interface MonthGridProps {
  readonly month: MonthKey;
  readonly buckets: readonly DayBucket[];
  readonly selectedDay: string | undefined;
  readonly onSelectDay: (day: string) => void;
  readonly locale?: string;
  /** The month's category marks (./category-marks.tsx); a day without them draws no dots. */
  readonly marks?: ReadonlyMap<string, CategoryMark>;
}

export function MonthGrid({
  month,
  buckets,
  selectedDay,
  onSelectDay,
  locale = displayLocale(),
  marks,
}: MonthGridProps) {
  const { t } = useTranslation('calendar');
  // Matched on the day key, so a today outside this month matches no cell and marks nothing.
  const today = useCalendarToday();
  const blanks = leadingBlanks(month);
  const cells: Array<DayBucket | null> = [
    ...Array.from({ length: blanks }, () => null),
    ...buckets,
  ];
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  const weeks: Array<Array<DayBucket | null>> = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }

  return (
    <div className={classes.monthWrap}>
      <table className={classes.month}>
        {/* The caption is what NAMES the table for a screen reader. Not decoration: without it the
            table announces as "table" and the month is nowhere in the announcement. Hidden from the
            eye only - the ‹ › row above prints the month. */}
        <VisuallyHidden component="caption">
          {t('grid.caption', { month: monthLabel(month, locale) })}
        </VisuallyHidden>
        <thead>
          <tr>
            {weekdays(locale).map((weekday, index) => (
              // Keyed by the column: two languages may abbreviate two days alike.
              <th key={String(index)} scope="col" className={classes.weekday}>
                {weekday}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIndex) => (
            // Keyed on the week's first REAL day where there is one: a month whose first row is all
            // padding cannot happen (the first of the month is always in row one), but a key derived
            // from `indexOf` would be a scan per row and would collide on two identical rows.
            <tr key={week.find((cell) => cell !== null)?.day ?? `week-${String(weekIndex)}`}>
              {week.map((cell, columnIndex) =>
                cell === null ? (
                  // A padding cell belongs to no day. It carries no control and no text, so it is
                  // skipped rather than announced as an empty button.
                  <td
                    key={`pad-${String(columnIndex)}`}
                    className={classes.cell}
                    aria-hidden="true"
                  >
                    <div className={classes.blank} />
                  </td>
                ) : (
                  <td key={cell.day} className={classes.cell}>
                    <DayCell
                      bucket={cell}
                      selected={cell.day === selectedDay}
                      today={cell.day === today}
                      onSelect={onSelectDay}
                      locale={locale}
                      marks={marks}
                    />
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayCell({
  bucket,
  selected,
  today,
  onSelect,
  locale,
  marks,
}: {
  bucket: DayBucket;
  selected: boolean;
  /** The day the platform's clock is in (./calendar-today.ts). */
  today: boolean;
  onSelect: (day: string) => void;
  // Always a language: `MonthGrid` takes the cabinet's (`displayLocale`, design 24, T16) when none
  // is given, never the browser's, so this cell's sentence and the caption read alike.
  locale: string;
  marks: ReadonlyMap<string, CategoryMark> | undefined;
}) {
  const { t } = useTranslation('calendar');
  const scheduled = bucket.load?.scheduled ?? 0;
  const band = loadBand(scheduled);
  const moonPhase = bucket.events.some((event) => event.kind === LUNAR_PHASE_KIND);
  const own = bucket.articles.length > 0;
  const dots = (bucket.load?.byCategory ?? [])
    .map((one) => marks?.get(one.category))
    .filter((mark): mark is CategoryMark => mark !== undefined)
    .sort((left, right) => left.mark - right.mark);
  const unshown =
    Math.max(0, bucket.events.length - SHOWN_PER_DAY) +
    Math.max(0, bucket.articles.length - SHOWN_PER_DAY);

  return (
    <button
      type="button"
      className={`${classes.day ?? ''} ${bucket.events.length > 0 ? (classes.dayMarked ?? '') : ''}`}
      onClick={() => {
        onSelect(bucket.day);
      }}
      aria-label={cellLabel(bucket, locale, today, t)}
      aria-pressed={selected}
      aria-current={today ? 'date' : undefined}
    >
      {/* Everything drawn here is for the eye; the button's name above says all of it in words. */}
      <span className={classes.dayFace} aria-hidden="true">
        <span className={classes.dayTop}>
          <span
            className={`${classes.dayNumber ?? ''} ${own ? (classes.dayNumberOwn ?? '') : ''} ${today ? (classes.dayNumberToday ?? '') : ''}`}
          >
            {dayNumber(bucket.day)}
          </span>
          {moonPhase ? <Moon size={12} className={classes.moon} aria-hidden /> : null}
        </span>

        {bucket.events.slice(0, SHOWN_PER_DAY).map((event) => (
          <span key={event.calendarEventId} className={classes.dayEvent}>
            {event.title}
          </span>
        ))}

        {own ? (
          <span className={classes.pieces}>
            {bucket.articles.slice(0, SHOWN_PER_DAY).map((article) => (
              <span key={article.articleId} className={classes.piece}>
                <span className={classes.pieceStatus}>{statusLabel(article.status)}</span>
                <span className={classes.pieceTitle}>{article.title}</span>
              </span>
            ))}
          </span>
        ) : null}

        {unshown > 0 ? (
          <span className={classes.more}>{t('grid.more', { count: unshown })}</span>
        ) : null}

        {scheduled > 0 || own ? (
          <span className={classes.dayFoot}>
            {scheduled > 0 ? (
              // The count is IN the pill, so the band never stands alone as an unexplained colour.
              <span className={`${classes.load ?? ''} ${BAND_CLASS[band] ?? ''}`}>
                <FileText size={10} className={classes.loadIcon} aria-hidden />
                {scheduled}
              </span>
            ) : null}
            {dots.length > 0 ? (
              <span className={classes.dots}>
                {dots.map((mark) => (
                  <CategoryDot key={mark.code} mark={mark.mark} />
                ))}
              </span>
            ) : null}
            {own ? <span className={classes.mineMark} /> : null}
          </span>
        ) : null}
      </span>
    </button>
  );
}
